import { useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { colors, USER_DATA, USER_UUID } from "../utils/constants";
import { useNavigate, useLocation } from "react-router-dom";
import ButtonLoading from "../components/ButtonLoading";
import { getUser, loginGoogle, logInWithEmailAndPassword } from "./../api/user";
import { toast } from "react-toastify";
import GoogleIcon from "@mui/icons-material/Google";
import "./index.css";
import logo from "../assets/logo-blue.png";
import Divider from "../components/Divider";
import Input from "../components/Input";
import AuthSidePanel from "../components/AuthSidePanel";
import {
  errorMessage,
  forgetPassword,
  forgetPasswordContainer,
  subtitleContainer,
  subtitle1,
  subtitle2,
  content,
  logo as logoStyle,
} from "./Login.style";

function Copyright({ ...props }) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {"Copyright © "}
      <Link color="inherit" href="https://qazando.com.br/">
        Qazando
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);

  const navigate = useNavigate();
  let location = useLocation();

  const from = location.state?.from?.pathname || "/";

  const loginWithGoogle = async () => {
    setLoading(true);

    await loginGoogle()
      .then((response: any) => {
        localStorage.clear();
        loadUserData(response.uid);
      })
      .catch((e) => {
        toast.error("Erro ao realizar login");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadUserData = (uid: String) => {
    getUser(uid)
      .then((response: any) => {
        localStorage.setItem(USER_DATA, JSON.stringify(response));
        localStorage.setItem(USER_UUID, response.uid);

        if (!from || from === "/") {
          if (response.client_id) {
            navigate(`/${response.client_id}`);
          } else {
            navigate("/events");
          }
        } else {
          navigate(from, { replace: true });
        }
      })
      .catch((err) => {
        console.log("err ao pegar dados do user: ", err);
      });
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    login();
  };

  const login = async () => {
    setLoginError(false);

    if (!email || !password) {
      setLoginError(true);
      return;
    }

    setLoading(true);
    logInWithEmailAndPassword(email, password)
      .then((response) => {
        setLoginError(false);
        localStorage.clear();
        loadUserData(response.user.uid);
      })
      .catch((e) => {
        setLoginError(true);
        toast.error("Erro ao realizar login");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box style={styles.container}>
      <CssBaseline />

      <AuthSidePanel />

      <Box sx={content}>
        <Box style={styles.formCol}>
          <img src={logo} style={logoStyle} alt="Logo" />

          <Box sx={subtitleContainer}>
            <span style={subtitle1}>Bem-vindo!</span>
            <span style={subtitle2}>Faça login e aproveite!</span>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            style={styles.form}
          >
            <Input
              title="E-MAIL"
              placeholder="Inserir e-mail"
              value={email}
              required
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <Input
              title="SENHA"
              placeholder="Inserir senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              type="password"
            />

            <div style={forgetPasswordContainer}>
              <Link href="/forget-password" variant="body2" style={forgetPassword}>
                Esqueci minha senha
              </Link>
            </div>

            {loginError && (
              <span style={errorMessage}>
                Erro ao realizar login. Verifique seus dados e tente novamente.
              </span>
            )}

            <ButtonLoading
              label="Acessar"
              loading={loading}
              className="landing-hover-lift"
              style={styles.primaryButton}
            />

            <div style={styles.registerRow}>
              <span style={styles.registerText}>Não tem uma conta?</span>
              <Link
                href="/register"
                variant="body2"
                style={{ ...forgetPassword, marginLeft: 6 }}
              >
                Inscreva-se
              </Link>
            </div>

            <div style={styles.dividerRow}>
              <Divider />
              <span style={styles.dividerLabel}>ou</span>
              <Divider />
            </div>

            <ButtonLoading
              label="Entrar com Google"
              loading={loading}
              onClick={loginWithGoogle}
              className="landing-hover-lift"
              style={styles.googleButton}
              icon={<GoogleIcon style={{ marginRight: 10 }} />}
            />
          </Box>

          <span style={styles.termsText}>
            Ao se cadastrar você aceita os{" "}
            <Link href="/terms-of-use">termos de uso</Link>
          </span>
        </Box>

        <Copyright sx={{ mt: 4 }} />
      </Box>
    </Box>
  );
}

const styles: any = {
  container: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "row",
  },

  formCol: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  form: {
    marginTop: 8,
    width: "100%",
    maxWidth: 420,
    padding: 0,
  },

  primaryButton: {
    marginTop: 24,
    width: "100%",
    height: 52,
    borderRadius: 100,
    color: colors.white,
  },

  registerRow: {
    display: "flex",
    justifyContent: "flex-start",
    marginTop: 20,
    fontSize: 14,
  },

  registerText: {
    color: "#5B6472",
    fontSize: 14,
  },

  dividerRow: {
    display: "flex",
    flexDirection: "row",
    marginTop: 28,
    marginBottom: 4,
    justifyContent: "center",
    alignItems: "center",
  },

  dividerLabel: {
    marginLeft: 20,
    marginRight: 20,
    color: "#9AA5B5",
    fontSize: 13,
  },

  googleButton: {
    marginTop: 16,
    width: "100%",
    height: 52,
    borderRadius: 100,
    backgroundColor: colors.white,
    color: "#14213D",
    border: "1.5px solid #E5E9F0",
  },

  termsText: {
    fontSize: 13,
    color: "#9AA5B5",
    marginTop: 24,
    display: "block",
  },
};
